import React from 'react';
import SEO from './SEO';

const PrivacyPolicy = () => {
  return (
    <>
      <SEO 
        title="Privacy Policy"
        description="Privacy Policy for Lown - Learn how we collect, use, and protect your personal information."
        keywords="privacy policy, data protection, personal information, Lown privacy"
        url="https://www.lown.app/privacy"
      />
      <div className="privacy-policy-page">
        <div className="privacy-policy-container animate-fade-in-up">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last updated: September 23, 2025</p>

          <section className="legal-section">
            <h2>Introduction</h2>
            <p>
              Lown respects your privacy. This policy explains what we collect, how we use it, and the choices you have.
            </p>
          </section>

          <section className="legal-section">
            <h2>What We Collect</h2>
            <ul>
              <li>
                <strong>Contact info you provide</strong> - your email address if you choose to enable email updates.
              </li>
              <li>
                <strong>Notification preferences</strong> - your selections for email, push, SMS, and topics such as feature updates, goal progress, or new content.
              </li>
              <li>
                <strong>In-app financial data (local or optional iCloud sync)</strong> - loan details, affordability inputs, amortization schedules, savings goals, and similar entries stored locally by default. If you sign in with Apple and enable sync, encrypted copies are stored in your private iCloud so your devices stay aligned.
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>How We Use Your Data</h2>
            <ul>
              <li>Operate core features you request, such as sending optional emails you opt into.</li>
              <li>Remember your notification preferences across sessions and devices.</li>
              <li>Keep loans, savings, and progress synchronized across Apple devices when you opt in to iCloud sync.</li>
              <li>Improve reliability and support, including error logs without personal content. We do not use your email for ads.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Storage and Retention</h2>
            <ul>
              <li>
                <strong>Local data</strong> such as loans, savings, and calculators stays on your device unless you remove it in-app or uninstall the app.
              </li>
              <li>
                <strong>iCloud sync data</strong> (when Apple Sign In is enabled) is stored in your private iCloud account, encrypted and managed under Apple's iCloud Terms. Removing the iCloud account or switching back to a local profile stops syncing and removes our access to that data.
              </li>
              <li>
                <strong>Contact info and preferences</strong> are stored securely on our servers only if you opt in. We retain them while your opt-in remains active and delete them when you unsubscribe or request deletion.
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Sharing</h2>
            <ul>
              <li>We do not sell your data.</li>
              <li>We may use service providers, such as email delivery partners, to process data on our behalf under privacy and security obligations.</li>
              <li>We may disclose information if required by law or to protect rights, safety, and security.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Your Choices</h2>
            <ul>
              <li>
                <strong>Email</strong> - unsubscribe anytime in Settings or via the link in our emails.
              </li>
              <li>
                <strong>Push notifications</strong> - control in-app toggles or iOS Settings &rarr; Notifications &rarr; Lown.
              </li>
              <li>
                <strong>iCloud sync</strong> - choose a local-only profile or sign in with Apple to sync. You can revert to local storage in Settings &rarr; Account if you prefer not to store data in iCloud.
              </li>
              <li>
                <strong>Data deletion</strong> - if you provided an email and created notification preferences on our server, request deletion at support@lown.app. Local data can be removed in-app or by uninstalling. For synced profiles, sign out of Apple in Settings &rarr; Account to stop syncing and remove Lown data from iCloud &rarr; Manage Storage if you want it deleted immediately.
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Children's Privacy</h2>
            <p>Lown is intended for users aged 13 and older and is not directed to children under 13.</p>
          </section>

          <section className="legal-section">
            <h2>Security</h2>
            <p>We use industry-standard safeguards such as HTTPS/TLS in transit. No method of transmission or storage is 100% secure.</p>
          </section>

          <section className="legal-section">
            <h2>Changes to This Policy</h2>
            <p>We may update this policy. We will post updates on this page and revise the "Last updated" date. If we materially change what we collect or how we use it, we will notify you in-app and give you the opportunity to review before continuing to use Lown.</p>
          </section>

          <section className="legal-section">
            <h2>Contact Us</h2>
            <p>Questions or requests? Email support@lown.app.</p>
          </section>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy; 
